import { lazy, type ComponentType } from "react";
import { safeSessionStorage } from "./safeStorage";

/**
 * A wrapper around React.lazy that adds automatic retries for failed chunk loads.
 * This resolves issues where users are on an old version of the app and try to navigate
 * to a route whose JS chunk has been removed or renamed after a new deployment.
 */
export const lazyWithRetry = (componentImport: () => Promise<{ default: ComponentType }>) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      safeSessionStorage.getItem("page-has-been-force-refreshed") || "false"
    );

    try {
      const component = await componentImport();
      safeSessionStorage.setItem("page-has-been-force-refreshed", "false");
      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        // Assume that the failure is due to an outdated chunk, and force a page reload.
        safeSessionStorage.setItem("page-has-been-force-refreshed", "true");
        window.location.reload();
        
        // Return a promise that never resolves, so React Suspense hangs until the reload completes
        return new Promise(() => {});
      }
      
      // If we already refreshed and it still failed, throw the error
      throw error;
    }
  });
